import { useCallback, useEffect, useRef, useState } from 'react';
import createCache from '@emotion/cache';
import {
  CLOSE_POPOUT_ON_NAVIGATION,
  DRAWER_POPOUT_HANDOFF_TTL_MS,
  DRAWER_REOPEN_POPOUT_EVENT,
} from '../DraggableDrawer.constants';

interface UseDrawerPopoutOptions {
  zoomLevel: number;
}

interface PendingPopoutAction {
  actionType: string;
  createdAt: number;
}

// Copies existing non-Emotion stylesheets and inline styles into the popout,
// then watches for dynamically added ones via MutationObserver.
const syncStylesToPopout = (popoutWindow: Window): MutationObserver => {
  document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]').forEach(link => {
    popoutWindow.document.head.appendChild(link.cloneNode(true));
  });

  document.querySelectorAll<HTMLStyleElement>('style:not([data-emotion])').forEach(style => {
    popoutWindow.document.head.appendChild(style.cloneNode(true));
  });

  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      Array.from(mutation.addedNodes).forEach(node => {
        if (node instanceof HTMLLinkElement && node.rel === 'stylesheet') {
          popoutWindow.document.head.appendChild(node.cloneNode(true));
        }
        if (node instanceof HTMLStyleElement && !node.getAttribute('data-emotion')) {
          popoutWindow.document.head.appendChild(node.cloneNode(true));
        }
      });
    });
  });
  observer.observe(document.head, { childList: true });
  return observer;
};

const useDrawerPopout = ({ zoomLevel }: UseDrawerPopoutOptions) => {
  const [isPopped, setIsPopped] = useState(false);
  const [popoutEmotionCache, setPopoutEmotionCache] = useState<ReturnType<typeof createCache> | null>(null);
  const [popoutHeight, setPopoutHeight] = useState<number | null>(null);

  const popoutWindowRef = useRef<Window | null>(null);
  const popoutContainerRef = useRef<HTMLDivElement | null>(null);
  const styleObserverRef = useRef<MutationObserver | null>(null);
  const popoutMouseUpRelayCleanupRef = useRef<(() => void) | null>(null);
  const pendingPopoutActionRef = useRef<PendingPopoutAction | null>(null);
  const shouldReopenPopoutRef = useRef(false);

  const cleanupPopout = useCallback(() => {
    popoutMouseUpRelayCleanupRef.current?.();
    popoutMouseUpRelayCleanupRef.current = null;
    styleObserverRef.current?.disconnect();
    styleObserverRef.current = null;
    setIsPopped(false);
    setPopoutEmotionCache(null);
    setPopoutHeight(null);
    popoutWindowRef.current = null;
    popoutContainerRef.current = null;
  }, []);

  const handlePopout = useCallback(() => {
    popoutMouseUpRelayCleanupRef.current?.();
    popoutMouseUpRelayCleanupRef.current = null;
    styleObserverRef.current?.disconnect();
    if (popoutWindowRef.current) {
      popoutWindowRef.current.close();
    }

    const popoutWindow = window.open(
      '',
      'DrawerPopout',
      'width=900,height=700,menubar=no,toolbar=no,location=no,status=no'
    );

    if (!popoutWindow) {
      return;
    }

    popoutWindowRef.current = popoutWindow;
    popoutWindow.document.title = document.title;

    styleObserverRef.current = syncStylesToPopout(popoutWindow);

    // MUI AppBar left offset fix for the popout context
    const popoutOverrides = popoutWindow.document.createElement('style');
    popoutOverrides.textContent = '.MuiAppBar-positionAbsolute { left: 0 !important; }';
    popoutWindow.document.head.appendChild(popoutOverrides);

    const cache = createCache({ key: 'popout', container: popoutWindow.document.head });

    const container = popoutWindow.document.createElement('div');
    container.id = 'popout-root';
    popoutWindow.document.documentElement.style.height = '100%';
    Object.assign(popoutWindow.document.body.style, {
      margin: '0',
      padding: '0',
      overflow: 'hidden',
      height: '100%',
      zoom: zoomLevel.toString(),
    });
    container.style.height = '100%';
    popoutWindow.document.body.appendChild(container);
    popoutContainerRef.current = container;

    const onResize = () => setPopoutHeight(popoutWindow.innerHeight);
    popoutWindow.addEventListener('resize', onResize);
    popoutWindow.addEventListener('beforeunload', cleanupPopout);

    // Some spreadsheet libs listen for mouseup on the main document only.
    // Relay popout mouseup to the main document so drag-selection can end correctly.
    const relayMouseUpToMainDocument = (event: MouseEvent) => {
      globalThis.document.dispatchEvent(
        new MouseEvent('mouseup', {
          bubbles: true,
          cancelable: true,
          button: event.button,
          buttons: event.buttons,
          clientX: event.clientX,
          clientY: event.clientY,
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
          shiftKey: event.shiftKey,
          altKey: event.altKey,
          view: globalThis.window,
        })
      );
    };
    popoutWindow.document.addEventListener('mouseup', relayMouseUpToMainDocument);
    popoutMouseUpRelayCleanupRef.current = () => {
      popoutWindow.document.removeEventListener('mouseup', relayMouseUpToMainDocument);
    };

    // Defer initial height read until the browser finishes laying out the window
    popoutWindow.requestAnimationFrame(() => {
      if (!popoutWindow.closed) {
        setPopoutHeight(popoutWindow.innerHeight);
      }
    });

    setPopoutEmotionCache(cache);
    setIsPopped(true);
  }, [cleanupPopout, zoomLevel]);

  const openOrFocusPopout = useCallback(() => {
    const existingPopout = popoutWindowRef.current;
    if (existingPopout && !existingPopout.closed) {
      existingPopout.focus();
      return;
    }
    handlePopout();
  }, [handlePopout]);

  const handlePopIn = useCallback(() => {
    if (popoutWindowRef.current) {
      popoutWindowRef.current.close();
    }
    cleanupPopout();
  }, [cleanupPopout]);

  const handoffPopoutActionToMain = useCallback(
    (actionType: string): boolean => {
      const popoutWindow = popoutWindowRef.current;
      if (!isPopped || !popoutWindow || popoutWindow.closed) {
        return false;
      }

      pendingPopoutActionRef.current = {
        actionType,
        createdAt: Date.now(),
      };
      window.focus();
      popoutWindow.close();
      return true;
    },
    [isPopped]
  );

  const consumePopoutActionFromMain = useCallback((): string | null => {
    const pendingAction = pendingPopoutActionRef.current;
    pendingPopoutActionRef.current = null;

    if (!pendingAction) {
      return null;
    }
    if (Date.now() - pendingAction.createdAt > DRAWER_POPOUT_HANDOFF_TTL_MS) {
      shouldReopenPopoutRef.current = false;
      return null;
    }
    shouldReopenPopoutRef.current = true;
    return pendingAction.actionType;
  }, []);

  const reopenPopoutIfNeeded = useCallback(() => {
    if (!shouldReopenPopoutRef.current) {
      return;
    }
    shouldReopenPopoutRef.current = false;
    openOrFocusPopout();
  }, [openOrFocusPopout]);

  // Keep popout body zoom in sync with the app zoom level
  useEffect(() => {
    if (isPopped && popoutWindowRef.current) {
      popoutWindowRef.current.document.body.style.zoom = zoomLevel.toString();
    }
  }, [zoomLevel, isPopped]);

  // Close popout when the main browser tab is closed or refreshed
  useEffect(() => {
    if (!CLOSE_POPOUT_ON_NAVIGATION) {
      return undefined;
    }
    const closePopoutOnUnload = () => {
      popoutWindowRef.current?.close();
    };
    window.addEventListener('beforeunload', closePopoutOnUnload);
    return () => {
      window.removeEventListener('beforeunload', closePopoutOnUnload);
    };
  }, []);

  // Reopen/focus popout when requested externally (e.g. after closing a modal in main app)
  useEffect(() => {
    const handleReopenPopout = () => openOrFocusPopout();
    window.addEventListener(DRAWER_REOPEN_POPOUT_EVENT, handleReopenPopout);
    return () => {
      window.removeEventListener(DRAWER_REOPEN_POPOUT_EVENT, handleReopenPopout);
    };
  }, [openOrFocusPopout]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      popoutMouseUpRelayCleanupRef.current?.();
      popoutMouseUpRelayCleanupRef.current = null;
      if (popoutWindowRef.current) {
        popoutWindowRef.current.close();
      }
      styleObserverRef.current?.disconnect();
    },
    []
  );

  // Close popout on route changes when configured
  const closeIfNavigating = useCallback(() => {
    if (CLOSE_POPOUT_ON_NAVIGATION && popoutWindowRef.current) {
      popoutWindowRef.current.close();
      cleanupPopout();
    }
  }, [cleanupPopout]);

  return {
    consumePopoutActionFromMain,
    handoffPopoutActionToMain,
    isPopped,
    popoutHeight,
    popoutContainerRef,
    popoutEmotionCache,
    reopenPopoutIfNeeded,
    handlePopout,
    handlePopIn,
    closeIfNavigating,
  };
};

export default useDrawerPopout;
